import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import services from '../data/services.jsx';
import { projects as portfolioProjects } from './Portfolio.jsx'; // Import the projects array directly
import { Helmet } from 'react-helmet-async';

const stats = [
  { label: 'Year Started', value: 2025 },
  { label: 'Projects Ongoing', value: 3 },
  { label: 'Stage', value: 'Initial' },
  { label: 'Team Members', value: 4 },
];

const testimonials = [
  {
    quote: 'Nayrix delivered a robust solution that transformed our business operations. Highly recommended!',
    author: 'Sarah M., CTO, FinTech Corp',
  },
  {
    quote: 'Their team is innovative, responsive, and truly understands enterprise needs.',
    author: 'James L., Head of IT, RetailX',
  },
  {
    quote: 'The SaaS platform they built for us is scalable and user-friendly. Great experience!',
    author: 'Priya S., Product Manager, SaaSify',
  },
];

const whyChooseUs = [
  {
    icon: (
      <svg className="w-8 h-8 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3" /></svg>
    ),
    title: 'Proven Expertise',
    desc: 'A decade of experience delivering enterprise-grade solutions.'
  },
  {
    icon: (
      <svg className="w-8 h-8 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" /></svg>
    ),
    title: 'Quality & Security',
    desc: 'We prioritize quality, security, and compliance in every project.'
  },
  {
    icon: (
      <svg className="w-8 h-8 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m4 0h-1v-4h-1" /></svg>
    ),
    title: 'End-to-End Support',
    desc: 'From ideation to launch and beyond, we support your growth.'
  },
];

const AnimatedCounter = ({ value }) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = 0;
    const end = value;
    if (start === end) return;
    let incrementTime = 1500 / end;
    let timer = setInterval(() => {
      start += 1;
      setCount(start);
      if (start === end) clearInterval(timer);
    }, incrementTime);
    return () => clearInterval(timer);
  }, [value]);
  return <span>{count}+</span>;
};

const Home = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0, prevX: 0, prevY: 0, isActive: false });
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [testimonialIndex, setTestimonialIndex] = useState(0);

  useEffect(() => {
    setIsDarkMode(window.matchMedia('(prefers-color-scheme: dark)').matches);
    const colorModeQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleColorModeChange = (e) => setIsDarkMode(e.matches);
    colorModeQuery.addEventListener('change', handleColorModeChange);
    return () => colorModeQuery.removeEventListener('change', handleColorModeChange);
  }, []);

  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let particles = [];
    const particleCount = 250;
    const colors = isDarkMode 
      ? ['#1a237e', '#0d47a1', '#01579b', '#0277bd', '#039be5', '#29b6f6', '#4fc3f7']
      : ['#e3f2fd', '#bbdefb', '#90caf9', '#64b5f6', '#42a5f3', '#2196f3', '#1e88e5'];
    const resizeCanvas = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      ctx.scale(dpr, dpr);
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    const handleMouseEnter = () => { mouseRef.current.isActive = true; };
    const handleMouseLeave = () => { mouseRef.current.isActive = false; };
    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current.prevX = mouseRef.current.x;
      mouseRef.current.prevY = mouseRef.current.y;
      mouseRef.current.x = e.clientX - rect.left;
      mouseRef.current.y = e.clientY - rect.top;
    };
    const handleTouchMove = (e) => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const touch = e.touches[0];
      mouseRef.current.prevX = mouseRef.current.x;
      mouseRef.current.prevY = mouseRef.current.y;
      mouseRef.current.x = touch.clientX - rect.left;
      mouseRef.current.y = touch.clientY - rect.top;
    };
    canvas.addEventListener('mouseenter', handleMouseEnter);
    canvas.addEventListener('mouseleave', handleMouseLeave);
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    class Particle {
      constructor() { this.reset(); }
      reset() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 4 + 2;
        this.color = colors[Math.floor(Math.random() * colors.length)];
        this.vx = (Math.random() - 0.5) * 2;
        this.vy = (Math.random() - 0.5) * 2;
        this.alpha = Math.random() * 0.5 + 0.5;
        this.baseSize = this.size;
        this.targetSize = this.size;
        this.trail = [];
        this.maxTrailLength = 8;
        this.rotation = Math.random() * Math.PI * 2;
        this.rotationSpeed = (Math.random() - 0.5) * 0.02;
        this.pulseSpeed = Math.random() * 0.02 + 0.01;
        this.pulsePhase = Math.random() * Math.PI * 2;
      }
      update() {
        this.trail.push({ x: this.x, y: this.y });
        if (this.trail.length > this.maxTrailLength) { this.trail.shift(); }
        this.rotation += this.rotationSpeed;
        const dx = mouseRef.current.x - this.x;
        const dy = mouseRef.current.y - this.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const maxDistance = 250;
        if (distance < maxDistance && mouseRef.current.isActive) {
          const mouseDx = mouseRef.current.x - mouseRef.current.prevX;
          const mouseDy = mouseRef.current.y - mouseRef.current.prevY;
          const mouseSpeed = Math.sqrt(mouseDx * mouseDx + mouseDy * mouseDy);
          const angle = Math.atan2(dy, dx);
          const force = (maxDistance - distance) / maxDistance;
          const velocityInfluence = Math.min(mouseSpeed * 0.8, 1.5);
          this.vx += Math.cos(angle) * force * (0.3 + velocityInfluence * 0.4);
          this.vy += Math.sin(angle) * force * (0.3 + velocityInfluence * 0.4);
          this.targetSize = this.baseSize * (1 + force * (1.5 + velocityInfluence));
          this.alpha = Math.min(0.9, 0.5 + force * 0.4);
        } else {
          this.targetSize = this.baseSize * (1 + Math.sin(this.pulsePhase) * 0.2);
          this.alpha = 0.5 + Math.sin(this.pulsePhase) * 0.2;
          this.pulsePhase += this.pulseSpeed;
        }
        this.size += (this.targetSize - this.size) * 0.1;
        this.x += this.vx;
        this.y += this.vy;
        if (this.x < 0 || this.x > canvas.width) {
          this.vx *= -0.85;
          this.x = Math.max(0, Math.min(this.x, canvas.width));
        }
        if (this.y < 0 || this.y > canvas.height) {
          this.vy *= -0.85;
          this.y = Math.max(0, Math.min(this.y, canvas.height));
        }
        this.vx += (Math.random() - 0.5) * 0.15;
        this.vy += (Math.random() - 0.5) * 0.15;
        const maxSpeed = 4;
        const speed = Math.sqrt(this.vx * this.vx + this.vy * this.vy);
        if (speed > maxSpeed) {
          this.vx = (this.vx / speed) * maxSpeed;
          this.vy = (this.vy / speed) * maxSpeed;
        }
        this.vx *= 0.985;
        this.vy *= 0.985;
      }
      draw() {
        this.trail.forEach((point, index) => {
          const trailAlpha = (index / this.trail.length) * this.alpha * 0.6;
          const trailSize = this.size * (index / this.trail.length);
          ctx.beginPath();
          ctx.arc(point.x, point.y, trailSize, 0, Math.PI * 2);
          ctx.fillStyle = this.color;
          ctx.globalAlpha = trailAlpha;
          ctx.fill();
          ctx.beginPath();
          ctx.arc(point.x, point.y, trailSize * 1.2, 0, Math.PI * 2);
          ctx.fillStyle = this.color;
          ctx.globalAlpha = trailAlpha * 0.3;
          ctx.fill();
        });
        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.rotate(this.rotation);
        ctx.beginPath();
        ctx.arc(0, 0, this.size, 0, Math.PI * 2);
        ctx.fillStyle = this.color;
        ctx.globalAlpha = this.alpha;
        ctx.fill();
        const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, this.size * 2);
        gradient.addColorStop(0, this.color);
        gradient.addColorStop(1, 'transparent');
        ctx.beginPath();
        ctx.arc(0, 0, this.size * 2, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.globalAlpha = this.alpha * 0.3;
        ctx.fill();
        ctx.restore();
      }
    }
    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle());
    }
    let lastTime = 0;
    const animate = (currentTime) => {
      const deltaTime = currentTime - lastTime;
      lastTime = currentTime;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.sort((a, b) => a.y - b.y);
      particles.forEach(particle => {
        particle.update();
        particle.draw();
      });
      animationRef.current = requestAnimationFrame(animate);
    };
    animate(0);
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      canvas.removeEventListener('mouseenter', handleMouseEnter);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('touchmove', handleTouchMove);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isDarkMode]);

  // Carousel logic for testimonials
  useEffect(() => {
    const timer = setInterval(() => {
      setTestimonialIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <>
      <Helmet>
        <title>Nayrix | Enterprise Software Solutions</title>
        <meta name="description" content="Custom enterprise software, SaaS, cloud, and API solutions to grow your business." />
        <meta property="og:title" content="Nayrix | Enterprise Software Solutions" />
        <meta property="og:description" content="Custom enterprise software, SaaS, cloud, and API solutions to grow your business." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://nayrix.com/" />
        <meta property="og:image" content="/white-logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Nayrix | Enterprise Software Solutions" />
        <meta name="twitter:description" content="Custom enterprise software, SaaS, cloud, and API solutions to grow your business." />
        <meta name="twitter:image" content="/white-logo.png" />
        <link rel="canonical" href="https://nayrix.com/" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Nayrix",
            "url": "https://nayrix.com",
            "logo": "/white-logo.png",
            "sameAs": [
              "https://www.linkedin.com/company/nayrix/",
              "https://github.com/nayrix-hub"
            ]
          }
        `}</script>
      </Helmet>
      <main className="relative">
        {/* Animated background gradient */}
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-primary-50 via-light-bg to-primary-100 dark:from-dark-bg dark:via-dark-bg/80 dark:to-dark-bg/60 animate-gradient-move" />
        <section className="relative min-h-[90vh] flex flex-col items-center justify-center overflow-hidden">
          <canvas
            ref={canvasRef}
            className="absolute inset-0 w-full h-full"
            style={{ opacity: 0.7 }}
          />
          <div className="container relative z-10 px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              className="text-center max-w-4xl mx-auto"
            >
              <motion.h1 
                className="text-5xl md:text-7xl font-bold mb-6 dark:text-dark-text text-light-text"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                Building the Future of
                <span className="block mt-2 dark:text-dark-accent text-light-accent">
                  Enterprise Software
                </span>
              </motion.h1>
              <motion.p 
                className="text-xl md:text-2xl dark:text-dark-text-secondary text-light-text-secondary mb-12 max-w-2xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                We create powerful, scalable solutions that transform how businesses operate in the digital age.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 justify-center"
              >
                <Link 
                  to="/contact" 
                  className="px-8 py-3 rounded-full dark:bg-dark-accent hover:bg-dark-accent-hover bg-light-accent hover:bg-light-accent-hover text-white transition-colors duration-300"
                >
                  Start Your Journey
                </Link>
                <Link 
                  to="/portfolio" 
                  className="px-8 py-3 rounded-full dark:border-dark-border dark:text-dark-text border-light-border text-light-text border-2 transition-colors duration-300"
                >
                  View Our Work
                </Link>
              </motion.div>
            </motion.div>
          </div>
          <motion.div 
            className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
          >
            <div className="flex flex-col items-center">
              <span className="text-sm dark:text-dark-text-secondary text-light-text-secondary mb-2">
                Scroll to explore
              </span>
              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className="w-6 h-10 dark:border-dark-border border-light-border border-2 rounded-full flex justify-center"
              >
                <motion.div
                  animate={{ y: [0, 12, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  className="w-1 h-2 dark:bg-dark-accent bg-light-accent rounded-full mt-2"
                />
              </motion.div>
            </div>
          </motion.div>
        </section>

        <motion.section
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="py-20 bg-light-bg dark:bg-dark-bg/50"
        >
          <div className="container mx-auto px-4 text-center max-w-3xl">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 dark:text-dark-text text-light-text">Empowering Enterprises for a Digital Future</h2>
            <p className="text-lg md:text-xl dark:text-dark-text-secondary text-light-text-secondary mb-6">
              Nayrix specializes in building robust, scalable, and innovative software solutions that drive business growth and operational excellence. Our mission is to help you thrive in the digital age.
            </p>
            <Link to="/about" className="inline-block mt-2 text-primary-600 dark:text-primary-400 font-medium underline-offset-4 hover:underline transition-colors duration-200">More details</Link>
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="py-12"
        >
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((stat, idx) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className="rounded-xl bg-white/80 dark:bg-dark-bg/70 backdrop-blur-md border border-neutral-200 dark:border-dark-border shadow-sm flex flex-col items-center justify-center py-6 px-2"
                  style={{ minHeight: '90px' }}
                >
                  <span className="text-2xl md:text-3xl font-display font-semibold text-primary-700 dark:text-primary-300 tracking-tight">
                    {stat.label === 'Year Started' ? '2025' : (typeof stat.value === 'number' ? <AnimatedCounter value={stat.value} /> : stat.value)}
                  </span>
                  <div className="text-xs md:text-sm font-medium text-neutral-700 dark:text-neutral-300 tracking-wide uppercase mt-1">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="py-20 bg-light-bg dark:bg-dark-bg/50"
        >
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold mb-10 text-center dark:text-dark-text text-light-text">Our Top Services</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {services.slice(0, 3).map((service, idx) => (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className="p-8 rounded-2xl dark:bg-dark-bg/50 bg-light-bg/50 border dark:border-dark-border border-light-border border text-center flex flex-col items-center"
                >
                  <div className="mb-4">{service.icon}</div>
                  <h3 className="text-xl font-semibold mb-2 dark:text-dark-text text-light-text">{service.title}</h3>
                  <p className="dark:text-dark-text-secondary text-light-text-secondary mb-4">{service.description}</p>
                  <button
                    onClick={() => setSelectedService(service)}
                    className="inline-block px-6 py-2 rounded-full bg-primary-600 text-white hover:bg-primary-700 transition-colors duration-300 mt-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    aria-label={`Learn more about ${service.title}`}
                  >
                    Learn More
                  </button>
                </motion.div>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/services" className="inline-block text-primary-600 dark:text-primary-400 font-medium underline-offset-4 hover:underline transition-colors duration-200">See all services</Link>
            </div>
          </div>
          {/* Modal Popup for Service Details */}
          {selectedService && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
              onClick={() => setSelectedService(null)}
              aria-modal="true"
              role="dialog"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="bg-light-bg dark:bg-dark-bg rounded-2xl shadow-2xl max-w-lg w-full p-8 relative border dark:border-dark-border border-light-border"
                onClick={e => e.stopPropagation()}
              >
                <button
                  onClick={() => setSelectedService(null)}
                  className="absolute top-4 right-4 text-xl dark:text-dark-text-secondary text-light-text-secondary hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none"
                  aria-label="Close service details"
                >
                  &times;
                </button>
                <div className="flex flex-col items-center text-center">
                  <div className="mb-4">{selectedService.icon}</div>
                  <h3 className="text-2xl font-bold mb-2 dark:text-dark-text text-light-text">{selectedService.title}</h3>
                  <p className="mb-4 dark:text-dark-text-secondary text-light-text-secondary">{selectedService.description}</p>
                  {selectedService.details && (
                    <div className="text-left w-full mt-4">
                      {selectedService.details.features && (
                        <div className="mb-3">
                          <h4 className="font-semibold mb-1 dark:text-dark-text text-light-text">Key Features:</h4>
                          <ul className="list-disc list-inside text-sm dark:text-dark-text-secondary text-light-text-secondary">
                            {selectedService.details.features.map((f, i) => <li key={i}>{f}</li>)}
                          </ul>
                        </div>
                      )}
                      {selectedService.details.benefits && (
                        <div className="mb-3">
                          <h4 className="font-semibold mb-1 dark:text-dark-text text-light-text">Benefits:</h4>
                          <ul className="list-disc list-inside text-sm dark:text-dark-text-secondary text-light-text-secondary">
                            {selectedService.details.benefits.map((b, i) => <li key={i}>{b}</li>)}
                          </ul>
                        </div>
                      )}
                      {selectedService.details.technologies && (
                        <div>
                          <h4 className="font-semibold mb-1 dark:text-dark-text text-light-text">Technologies:</h4>
                          <ul className="list-disc list-inside text-sm dark:text-dark-text-secondary text-light-text-secondary">
                            {selectedService.details.technologies.map((t, i) => <li key={i}>{t}</li>)}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="py-20 bg-light-bg dark:bg-dark-bg"
        >
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-10 dark:text-dark-text text-light-text">What Our Clients Say</h2>
            <div className="relative">
              <motion.div
                key={testimonialIndex}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.5 }}
                className="p-8 rounded-2xl dark:bg-dark-bg/50 bg-light-bg/50 border dark:border-dark-border border-light-border border flex flex-col items-center"
              >
                <p className="text-lg md:text-xl font-medium mb-4 dark:text-dark-text text-light-text">“{testimonials[testimonialIndex].quote}”</p>
                <footer className="text-sm dark:text-dark-text-secondary text-light-text-secondary">{testimonials[testimonialIndex].author}</footer>
              </motion.div>
              <div className="flex justify-center gap-2 mt-6">
                {testimonials.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setTestimonialIndex(idx)}
                    className={`w-3 h-3 rounded-full ${testimonialIndex === idx ? 'bg-primary-600 dark:bg-primary-400' : 'bg-gray-300 dark:bg-gray-600'} transition-all`}
                    aria-label={`Go to testimonial ${idx + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="py-20 bg-light-bg dark:bg-dark-bg/50"
        >
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold mb-10 text-center dark:text-dark-text text-light-text">Why Choose Nayrix?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {whyChooseUs.map((item, idx) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className="p-8 rounded-2xl dark:bg-dark-bg/50 bg-light-bg/50 border dark:border-dark-border border-light-border border text-center flex flex-col items-center"
                >
                  <div className="mb-4">{item.icon}</div>
                  <h3 className="text-xl font-semibold mb-2 dark:text-dark-text text-light-text">{item.title}</h3>
                  <p className="dark:text-dark-text-secondary text-light-text-secondary">{item.desc}</p>
                </motion.div>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/about" className="inline-block text-primary-600 dark:text-primary-400 font-medium underline-offset-4 hover:underline transition-colors duration-200">Learn more</Link>
            </div>
          </div>
        </motion.section>

        {/* Latest Projects Preview */}
        <motion.section
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="py-20 bg-light-bg dark:bg-dark-bg/50"
        >
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold mb-10 text-center dark:text-dark-text text-light-text">Latest Projects</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {portfolioProjects.slice(0, 3).map((project, idx) => (
                <motion.div
                  key={project.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className="rounded-2xl overflow-hidden shadow-lg cursor-pointer group"
                  onClick={() => setSelectedProject(project)}
                >
                  <div className="relative aspect-video overflow-hidden">
                    <img src={project.image} alt={project.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-70 group-hover:opacity-80 transition-opacity duration-300" />
                  </div>
                  <div className="p-6 bg-light-bg dark:bg-dark-bg">
                    <span className="text-sm dark:text-dark-accent text-light-accent mb-2 block">{project.category}</span>
                    <h3 className="text-xl font-semibold mb-2 dark:text-dark-text text-light-text">{project.title}</h3>
                    <p className="dark:text-dark-text-secondary text-light-text-secondary">{project.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/portfolio" className="inline-block text-primary-600 dark:text-primary-400 font-medium underline-offset-4 hover:underline transition-colors duration-200">See all projects</Link>
            </div>
          </div>
          {/* Modal for Project Quick-View */}
          <AnimatePresence>
            {selectedProject && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md"
                onClick={() => setSelectedProject(null)}
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  className="bg-light-bg dark:bg-dark-bg rounded-2xl max-w-2xl w-full shadow-2xl border dark:border-dark-border border-light-border"
                  onClick={e => e.stopPropagation()}
                >
                  <div className="relative aspect-video overflow-hidden rounded-t-2xl">
                    <img src={selectedProject.image} alt={selectedProject.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-8">
                    <span className="text-sm dark:text-dark-accent text-light-accent mb-2 block">{selectedProject.category}</span>
                    <h3 className="text-2xl font-semibold mb-4 dark:text-dark-text text-light-text">{selectedProject.title}</h3>
                    <p className="dark:text-dark-text-secondary text-light-text-secondary mb-6">{selectedProject.description}</p>
                    <button
                      onClick={() => setSelectedProject(null)}
                      className="px-6 py-2 rounded-full border dark:border-dark-border dark:text-dark-text border-light-border text-light-text border-2 transition-colors duration-300"
                    >
                      Close
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.section>
      </main>
    </>
  );
};

export default Home;
